import React, { useCallback, useEffect, useState } from "react";
import {
  Drawer,
  Switch,
  Transfer,
  Button,
  Popconfirm,
  Typography,
  Spin,
  Divider,
  Alert,
  Modal,
  Tag,
  Checkbox,
} from "antd";
import {
  getClassroomPrograms,
  updateClassroomTeacherScope,
  getClassroomsBySchoolId,
} from "../../../../services/classrooms/classrooms.service";
import { useNotification } from "../../../../hooks/useNotification";

const { Text, Title } = Typography;

interface ClassTeacherScopeDrawerProps {
  open: boolean;
  onClose: () => void;
  schoolId: number;
  // null while no row has been picked yet — the drawer stays closed in that
  // case (guarded by the parent), kept nullable for prop-wiring convenience.
  classroomId: number | null;
  classroomLabel: string;
}

interface ProgramOption {
  key: string;
  title: string;
  // Inglés es la única materia con perfil propio (SCRUM-800), así que se
  // marca al cargar. Se reconoce por `subject.code`, que el backend fija en
  // 'english' — el nombre es editable por escuela y no sirve de llave.
  isEnglish: boolean;
}

const ENGLISH_SUBJECT_CODE = "english";

/**
 * Los dos perfiles del ticket, listos en un click: "Titular" son todas las
 * materias del grupo menos inglés, y "Solo inglés" es únicamente inglés.
 * Antes había que armarlos moviendo materia por materia en el Transfer, que es
 * lo que volvía manual marcar a un profesor de inglés. Es el mismo atajo que
 * CascadaAsignaturas ya ofrece en /usuarios del frontend.
 *
 * Precargan la selección y la dejan sin guardar: el admin revisa y confirma con
 * Guardar, igual que cualquier edición del Transfer.
 */
const profileKeys = (
  options: ProgramOption[],
  profile: "titular" | "english",
): string[] =>
  options
    .filter((option) =>
      profile === "english" ? option.isEnglish : !option.isEnglish,
    )
    .map((option) => option.key);

// Per-teacher local edit state, seeded by deriving — from the licensed
// programs' scoped_teacher_ids arrays (GET /classrooms/:id/programs) —
// which programs currently scope this teacher, and only committed back via
// "Guardar" — mirrors ClassProgramsDrawer's edit-then-save pattern.
interface TeacherEntry {
  teacher_id: number;
  teacher_name: string;
  // Server truth: program ids that currently scope this teacher. Empty
  // means unrestricted — there is no parent row to distinguish "restricted
  // to zero programs" from "never scoped", so both read back the same way.
  savedProgramIds: number[];
  // Best-known restricted subset, kept in local memory across unrestricted
  // toggles within this drawer session so re-restricting does not start
  // from a blank pane — mirrors ClassProgramsDrawer's lastKnownStudentIds.
  // Does NOT survive a reload of this drawer.
  lastKnownProgramIds: number[];
  localProgramIds: string[];
  // Explicit admin intent via the switch. Emptying the Transfer down to zero
  // programs is ALSO treated as unrestricted for save/confirm purposes (see
  // isEffectivelyUnrestricted) even while this stays false, because the
  // backend cannot represent "restricted to nothing" as a state distinct
  // from "unrestricted" — see classrooms.types.ts's doc comment.
  localUnrestricted: boolean;
  saving: boolean;
}

// A teacher is effectively unrestricted whenever the switch says so OR the
// pending selection is empty — both save as program_ids: [] and neither is
// a "block this teacher" state (that state cannot be represented at all).
const isEffectivelyUnrestricted = (entry: TeacherEntry): boolean =>
  entry.localUnrestricted || entry.localProgramIds.length === 0;

const isDirty = (entry: TeacherEntry): boolean => {
  const savedUnrestricted = entry.savedProgramIds.length === 0;
  if (isEffectivelyUnrestricted(entry)) return !savedUnrestricted;
  const local = [...entry.localProgramIds].map(Number).sort((a, b) => a - b);
  const saved = [...entry.savedProgramIds].sort((a, b) => a - b);
  return JSON.stringify(local) !== JSON.stringify(saved);
};

// Destructive direction: narrowing an unrestricted teacher to specific
// materias, or dropping a materia they currently have access to. Moving TO
// unrestricted (explicitly or by emptying the Transfer) never reduces
// access, so it gets the lighter confirmation below instead.
const wouldReduceAccess = (entry: TeacherEntry): boolean => {
  if (isEffectivelyUnrestricted(entry)) return false;
  if (entry.savedProgramIds.length === 0) return true;
  const nextIds = new Set(entry.localProgramIds.map(Number));
  return entry.savedProgramIds.some((id) => !nextIds.has(id));
};

const needsSaveConfirm = (entry: TeacherEntry): boolean =>
  isDirty(entry) && wouldReduceAccess(entry);

const idsEqual = (a: number[], b: number[]): boolean =>
  JSON.stringify([...a].sort((x, y) => x - y)) ===
  JSON.stringify([...b].sort((x, y) => x - y));

// Estado legible del alcance guardado, para que la oferta en lote muestre qué
// decisión previa tiene cada profesor antes de tocarla.
const scopeStateLabel = (
  saved: number[],
  englishIds: number[],
  titularIds: number[],
): string => {
  if (saved.length === 0) return "Sin restricciones";
  if (idsEqual(saved, englishIds)) return "Solo inglés";
  if (idsEqual(saved, titularIds)) return "Titular";
  return "Personalizado";
};

interface BulkCandidate {
  teacher_id: number;
  teacher_name: string;
  stateLabel: string;
  checked: boolean;
}

interface BulkOfferState {
  sourceName: string;
  candidates: BulkCandidate[];
  applying: boolean;
}

// Safe-direction confirm (lighter ceremony, mirrors the sibling drawer's
// needsRevertConfirm): only relevant when the teacher was genuinely
// restricted before (a non-empty saved subset) and is now moving to
// unrestricted.
const needsUnrestrictedConfirm = (entry: TeacherEntry): boolean =>
  isDirty(entry) &&
  isEffectivelyUnrestricted(entry) &&
  entry.savedProgramIds.length > 0;

/**
 * "Profesores" drawer for a single group (ClassesTab management row):
 * per teacher of the group, either unrestricted (teaches every materia
 * licensed to the group — the common case) or restricted to an explicit
 * subset of materias, managed here via a Transfer of the group's licensed
 * programs. Sibling of ClassProgramsDrawer (student enrollment); kept as a
 * separate drawer because the resource shape is per (classroom, teacher),
 * not per (classroom, program) — see classrooms.service.ts's doc comment.
 */
const ClassTeacherScopeDrawer: React.FC<ClassTeacherScopeDrawerProps> = ({
  open,
  onClose,
  schoolId,
  classroomId,
  classroomLabel,
}) => {
  const { notify } = useNotification();
  // Context-based Modal API: AntD v5 static Modal.confirm does not render
  // under React 19 (same pattern as ClassProgramsDrawer).
  const [modal, modalContextHolder] = Modal.useModal();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [programOptions, setProgramOptions] = useState<ProgramOption[]>([]);
  const [teacherEntries, setTeacherEntries] = useState<TeacherEntry[]>([]);
  const [bulkOffer, setBulkOffer] = useState<BulkOfferState | null>(null);

  useEffect(() => {
    if (!open || !classroomId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [programs, classrooms] = await Promise.all([
          getClassroomPrograms(classroomId),
          getClassroomsBySchoolId(schoolId),
        ]);
        if (cancelled) return;

        setProgramOptions(
          programs.map((p) => ({
            key: String(p.program_id),
            title: p.subject.name,
            isEnglish: p.subject.code === ENGLISH_SUBJECT_CODE,
          }))
        );

        const classroom = classrooms.find((c) => c.id === classroomId);
        const teachers = (classroom?.users ?? []).filter(
          (u: any) => u.role === "TEACHER"
        );
        setTeacherEntries(
          teachers
            .map((t: any) => {
              const scopedProgramIds = programs
                .filter((p) => p.scoped_teacher_ids.includes(t.id))
                .map((p) => p.program_id);
              return {
                teacher_id: t.id,
                teacher_name: `${t.first_name} ${t.last_name}`,
                savedProgramIds: scopedProgramIds,
                lastKnownProgramIds: scopedProgramIds,
                localProgramIds: scopedProgramIds.map(String),
                localUnrestricted: scopedProgramIds.length === 0,
                saving: false,
              };
            })
            .sort((a, b) => a.teacher_name.localeCompare(b.teacher_name))
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading classroom teacher scope:", error);
        setLoadError(true);
        notify(
          "error",
          "Error",
          "No se pudieron cargar los profesores del grupo."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classroomId, schoolId, reloadToken]);

  const updateEntry = (teacherId: number, patch: Partial<TeacherEntry>) => {
    setTeacherEntries((prev) =>
      prev.map((entry) =>
        entry.teacher_id === teacherId ? { ...entry, ...patch } : entry
      )
    );
  };

  const handleSwitchChange = (teacherId: number, checked: boolean) => {
    setTeacherEntries((prev) =>
      prev.map((entry) => {
        if (entry.teacher_id !== teacherId) return entry;
        if (checked) {
          const remembered =
            entry.localProgramIds.length > 0
              ? entry.localProgramIds.map(Number)
              : entry.lastKnownProgramIds;
          return {
            ...entry,
            localUnrestricted: true,
            lastKnownProgramIds: remembered,
            localProgramIds: [],
          };
        }
        // Switching to restricted: seed the Transfer from the best-known
        // subset so the admin never lands on a blank pane by default.
        const seeded =
          entry.localProgramIds.length > 0
            ? entry.localProgramIds
            : entry.lastKnownProgramIds.map(String);
        return { ...entry, localUnrestricted: false, localProgramIds: seeded };
      })
    );
  };

  const handleTransferChange = (teacherId: number, nextTargetKeys: string[]) => {
    updateEntry(teacherId, { localProgramIds: nextTargetKeys });
  };

  const handleApplyProfile = (
    teacherId: number,
    profile: "titular" | "english",
  ) => {
    updateEntry(teacherId, {
      localProgramIds: profileKeys(programOptions, profile),
    });
  };

  // Explicit discard of a pending edit — used ONLY by the close-guard's
  // "Cerrar sin guardar" action below. Popconfirm's own Cancel button must
  // NEVER call this: "Cancelar" on a save confirmation means "no guardar
  // todavía", not "throw away what I curated".
  const handleDiscardPendingEdit = (teacherId: number) => {
    setTeacherEntries((prev) =>
      prev.map((entry) =>
        entry.teacher_id === teacherId
          ? {
              ...entry,
              localUnrestricted: entry.savedProgramIds.length === 0,
              localProgramIds: entry.savedProgramIds.map(String),
            }
          : entry
      )
    );
  };

  // Persiste el alcance de un profesor y refleja la verdad del servidor en el
  // estado local. Las notificaciones quedan en los llamadores: el guardado
  // individual conserva su toast y el lote las agrega.
  const persistScope = async (
    teacherId: number,
    programIds: number[],
  ): Promise<number[]> => {
    updateEntry(teacherId, { saving: true });
    try {
      const result = await updateClassroomTeacherScope(classroomId!, teacherId, {
        program_ids: programIds,
      });
      setTeacherEntries((prev) =>
        prev.map((entry) =>
          entry.teacher_id !== teacherId
            ? entry
            : {
                ...entry,
                saving: false,
                savedProgramIds: result.program_ids,
                // Keep the best-known restricted subset around even when the
                // save just cleared it, so re-restricting in this same session
                // starts from the last known list instead of an empty one.
                lastKnownProgramIds:
                  result.program_ids.length > 0
                    ? result.program_ids
                    : entry.lastKnownProgramIds,
                localUnrestricted: result.program_ids.length === 0,
                localProgramIds: result.program_ids.map(String),
              },
        ),
      );
      return result.program_ids;
    } catch (error) {
      updateEntry(teacherId, { saving: false });
      throw error;
    }
  };

  // Al quedar un profesor de solo inglés, el resto del grupo suele ser
  // titular: se ofrece aplicarlo en lote en vez de ir profesor por profesor.
  // Solo se preseleccionan los que no tienen perfil configurado — una decisión
  // explícita previa nunca se pisa sin marcarla a mano.
  const maybeOfferBulkTitular = (source: TeacherEntry, savedIds: number[]) => {
    const englishIds = profileKeys(programOptions, "english").map(Number);
    const titularIds = profileKeys(programOptions, "titular").map(Number);
    if (savedIds.length === 0 || !idsEqual(savedIds, englishIds)) return;
    if (titularIds.length === 0) return;

    const candidates = teacherEntries.filter(
      (entry) => entry.teacher_id !== source.teacher_id,
    );
    if (candidates.length === 0) return;

    setBulkOffer({
      sourceName: source.teacher_name,
      applying: false,
      candidates: candidates.map((entry) => ({
        teacher_id: entry.teacher_id,
        teacher_name: entry.teacher_name,
        stateLabel: scopeStateLabel(
          entry.savedProgramIds,
          englishIds,
          titularIds,
        ),
        checked: entry.savedProgramIds.length === 0,
      })),
    });
  };

  const handleSave = async (teacherId: number) => {
    const entry = teacherEntries.find((e) => e.teacher_id === teacherId);
    if (!classroomId || !entry) return;

    try {
      const programIds = isEffectivelyUnrestricted(entry)
        ? []
        : entry.localProgramIds.map(Number);
      const savedIds = await persistScope(teacherId, programIds);
      notify("success", "Éxito", "Alcance del profesor actualizado correctamente.");
      maybeOfferBulkTitular(entry, savedIds);
    } catch (error) {
      console.error("Error updating classroom teacher scope:", error);
      notify("error", "Error", "No se pudo guardar el alcance del profesor.");
    }
  };

  const toggleBulkCandidate = (teacherId: number, checked: boolean) => {
    setBulkOffer((prev) =>
      prev && {
        ...prev,
        candidates: prev.candidates.map((candidate) =>
          candidate.teacher_id === teacherId
            ? { ...candidate, checked }
            : candidate,
        ),
      },
    );
  };

  const handleBulkApplyTitular = async () => {
    if (!bulkOffer || !classroomId) return;
    const titularIds = profileKeys(programOptions, "titular").map(Number);
    const chosen = bulkOffer.candidates.filter((c) => c.checked);

    setBulkOffer((prev) => prev && { ...prev, applying: true });
    const failed: string[] = [];
    for (const candidate of chosen) {
      try {
        await persistScope(candidate.teacher_id, titularIds);
      } catch (error) {
        console.error("Error applying bulk titular scope:", error);
        failed.push(candidate.teacher_name);
      }
    }
    setBulkOffer(null);

    const applied = chosen.length - failed.length;
    if (applied > 0) {
      notify(
        "success",
        "Éxito",
        `Perfil Titular aplicado a ${applied} ${applied === 1 ? "profesor" : "profesores"}.`,
      );
    }
    if (failed.length > 0) {
      notify(
        "error",
        "Error",
        `No se pudo aplicar el perfil a: ${failed.join(", ")}.`,
      );
    }
  };

  const anyDirty = teacherEntries.some(isDirty);
  // While a save is in flight the drawer is not dismissable — mirrors
  // ClassProgramsDrawer.
  const anySaving = teacherEntries.some((entry) => entry.saving);

  const handleRequestClose = useCallback(() => {
    if (anySaving) return;
    if (!anyDirty) {
      onClose();
      return;
    }
    modal.confirm({
      title: "Cambios sin guardar",
      content:
        "Hay cambios sin guardar en este panel de profesores. Si se cierra ahora, se perderán.",
      okText: "Cerrar sin guardar",
      cancelText: "Seguir editando",
      onOk: () => {
        teacherEntries.forEach((entry) => handleDiscardPendingEdit(entry.teacher_id));
        onClose();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyDirty, anySaving, onClose]);

  return (
    <Drawer
      title={`Profesores — ${classroomLabel}`}
      open={open}
      onClose={handleRequestClose}
      width={520}
      closable={!anySaving}
      maskClosable={!anySaving}
      keyboard={!anySaving}
      destroyOnClose
    >
      {modalContextHolder}
      {loading ? (
        <Spin />
      ) : loadError ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los profesores del grupo."
          description="Puede deberse a un problema de conexión o del servidor."
          action={
            <Button size="small" onClick={() => setReloadToken((t) => t + 1)}>
              Reintentar
            </Button>
          }
        />
      ) : teacherEntries.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="Este grupo no tiene profesores asignados."
        />
      ) : (
        teacherEntries.map((entry, index) => {
          const dirty = isDirty(entry);
          const saveConfirmNeeded = needsSaveConfirm(entry);
          const unrestrictedConfirmNeeded =
            !saveConfirmNeeded && needsUnrestrictedConfirm(entry);

          const guardarButton = (
            <Button
              type="primary"
              disabled={!dirty || entry.saving}
              loading={entry.saving}
              onClick={() => {
                if (!saveConfirmNeeded && !unrestrictedConfirmNeeded) {
                  handleSave(entry.teacher_id);
                }
              }}
            >
              Guardar
            </Button>
          );

          let guardarControl = guardarButton;
          if (saveConfirmNeeded) {
            const wasUnrestricted = entry.savedProgramIds.length === 0;
            guardarControl = (
              <Popconfirm
                title={
                  wasUnrestricted
                    ? "Esta acción restringirá a este profesor a solo las asignaturas seleccionadas: perderá acceso para ver y calificar las demás asignaturas del grupo. ¿Continuar?"
                    : "Esta acción quitará a este profesor el acceso a una o más asignaturas que actualmente puede ver y calificar. ¿Continuar?"
                }
                okText="Sí, continuar"
                cancelText="Cancelar"
                onConfirm={() => handleSave(entry.teacher_id)}
              >
                {guardarButton}
              </Popconfirm>
            );
          } else if (unrestrictedConfirmNeeded) {
            guardarControl = (
              <Popconfirm
                title="Este profesor podrá ver y calificar todas las asignaturas del grupo, incluidas las que se agreguen más adelante. ¿Continuar?"
                okText="Sí, activar sin restricciones"
                cancelText="Cancelar"
                onConfirm={() => handleSave(entry.teacher_id)}
              >
                {guardarButton}
              </Popconfirm>
            );
          }

          return (
            <div key={entry.teacher_id} data-testid="teacher-row">
              {index > 0 && <Divider />}
              <Title level={5} style={{ marginBottom: 4 }}>
                {entry.teacher_name}
              </Title>
              {/* A dirty row must never be mistaken for the true server
                  state — the switch/Transfer alone can look identical to a
                  saved state that isn't. */}
              {dirty && (
                <Tag color="warning" style={{ marginLeft: 8 }}>
                  Sin guardar
                </Tag>
              )}

              {/* Unrestricted is the common case in production — shown as a
                  positive state, never as an error or empty placeholder.
                  Only shown while the switch itself reads unrestricted; the
                  "emptied Transfer" variant of the same outcome gets its own
                  explanatory Alert next to the Transfer instead, so the
                  consequence is explained once, not duplicated. */}
              {entry.localUnrestricted && (
                <div style={{ margin: "8px 0" }}>
                  <Tag color="success">Sin restricciones</Tag>
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    Imparte todas las asignaturas del grupo.
                  </Text>
                </div>
              )}

              <div style={{ margin: "12px 0" }}>
                <Switch
                  checked={entry.localUnrestricted}
                  disabled={entry.saving}
                  onChange={(checked) =>
                    handleSwitchChange(entry.teacher_id, checked)
                  }
                />
                <span style={{ marginLeft: 8 }}>
                  Sin restricciones (todas las asignaturas)
                </span>
              </div>

              {!entry.localUnrestricted && (
                <>
                  {/* Atajos de perfil: sólo precargan la selección, el guardado
                      sigue siendo explícito. "Solo inglés" se omite cuando el
                      grupo no licencia inglés, para no ofrecer un atajo que
                      dejaría la selección vacía (= sin restricciones). */}
                  <div style={{ marginBottom: 8, display: "flex", gap: 8 }}>
                    <Button
                      size="small"
                      disabled={entry.saving}
                      onClick={() =>
                        handleApplyProfile(entry.teacher_id, "titular")
                      }
                    >
                      Titular
                    </Button>
                    {programOptions.some((option) => option.isEnglish) && (
                      <Button
                        size="small"
                        disabled={entry.saving}
                        onClick={() =>
                          handleApplyProfile(entry.teacher_id, "english")
                        }
                      >
                        Solo inglés
                      </Button>
                    )}
                    {/* "Ver todo" se materializa como sin restricciones (cero
                        filas), no como asignación explícita de todo: así una
                        materia licenciada más adelante entra sola. */}
                    <Button
                      size="small"
                      disabled={entry.saving}
                      onClick={() =>
                        handleSwitchChange(entry.teacher_id, true)
                      }
                    >
                      Sin restricciones
                    </Button>
                  </div>
                  <Transfer
                    dataSource={programOptions}
                    targetKeys={entry.localProgramIds}
                    disabled={entry.saving}
                    onChange={(nextTargetKeys) =>
                      handleTransferChange(
                        entry.teacher_id,
                        nextTargetKeys as string[]
                      )
                    }
                    render={(item) => item.title}
                    titles={["Asignaturas del grupo", "Asignadas"]}
                    listStyle={{ width: 220, height: 240 }}
                  />
                  {entry.localProgramIds.length === 0 && (
                    <Alert
                      style={{ marginTop: 8 }}
                      type="info"
                      showIcon
                      message="Sin asignaturas seleccionadas: este profesor quedará SIN restricciones"
                      description="Si guarda así, el profesor podrá impartir todas las asignaturas del grupo — no se le bloqueará el acceso."
                    />
                  )}
                </>
              )}

              <div style={{ marginTop: 12 }}>{guardarControl}</div>
            </div>
          );
        })
      )}

      <Modal
        open={!!bulkOffer}
        title="¿Aplicar Titular al resto del grupo?"
        okText={`Aplicar Titular a ${
          bulkOffer?.candidates.filter((c) => c.checked).length ?? 0
        } ${
          (bulkOffer?.candidates.filter((c) => c.checked).length ?? 0) === 1
            ? "profesor"
            : "profesores"
        }`}
        cancelText="Omitir"
        okButtonProps={{
          disabled:
            (bulkOffer?.candidates.filter((c) => c.checked).length ?? 0) === 0,
          loading: bulkOffer?.applying,
        }}
        cancelButtonProps={{ disabled: bulkOffer?.applying }}
        closable={!bulkOffer?.applying}
        maskClosable={false}
        keyboard={!bulkOffer?.applying}
        onOk={handleBulkApplyTitular}
        onCancel={() => {
          if (!bulkOffer?.applying) setBulkOffer(null);
        }}
      >
        <Text>
          {bulkOffer?.sourceName} quedó con el perfil "Solo inglés". Puede
          aplicar el perfil Titular (todas las asignaturas del grupo excepto
          inglés) a los demás profesores en un solo paso.
        </Text>
        <div style={{ margin: "16px 0" }}>
          {bulkOffer?.candidates.map((candidate) => (
            <div
              key={candidate.teacher_id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <Checkbox
                checked={candidate.checked}
                disabled={bulkOffer.applying}
                onChange={(event) =>
                  toggleBulkCandidate(
                    candidate.teacher_id,
                    event.target.checked,
                  )
                }
              >
                {candidate.teacher_name}
              </Checkbox>
              <Tag
                color={
                  candidate.stateLabel === "Sin restricciones"
                    ? "success"
                    : "default"
                }
              >
                {candidate.stateLabel}
              </Tag>
            </div>
          ))}
        </div>
        <Text type="secondary">
          Solo se preseleccionan los profesores sin un perfil configurado. Los
          que ya tienen uno no se modifican, salvo que se marquen a mano.
        </Text>
      </Modal>
    </Drawer>
  );
};

export default ClassTeacherScopeDrawer;
